import { useCallback, useEffect, useRef, useState } from "react";

interface UseHorizontalRailOptions {
    itemCount: number;
    cardSelector: string;
    isRtl: boolean;
    gap?: number;
}

export const useHorizontalRail = ({
    itemCount,
    cardSelector,
    isRtl,
    gap = 32,
}: UseHorizontalRailOptions) => {
    const railRef = useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);
    const [needsScroll, setNeedsScroll] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);

    const updateScrollState = useCallback(() => {
        const el = railRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const overflowing = maxScroll > 4;
        setNeedsScroll(overflowing);
        const scrollLeft = Math.abs(el.scrollLeft);
        setCanPrev(overflowing && scrollLeft > 4);
        setCanNext(overflowing && scrollLeft < maxScroll - 4);
        setScrollProgress(overflowing ? Math.min(1, scrollLeft / maxScroll) : 0);

        const firstCard = el.querySelector<HTMLElement>(cardSelector);
        if (firstCard) {
            const cardWidth = firstCard.offsetWidth;
            const idx = Math.round(scrollLeft / (cardWidth + gap));
            setActiveIndex(Math.min(Math.max(0, idx), itemCount - 1));
        }
    }, [cardSelector, gap, itemCount]);

    useEffect(() => {
        updateScrollState();
        const el = railRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);
        return () => {
            el.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [updateScrollState]);

    const scrollToIndex = useCallback(
        (targetIndex: number) => {
            const el = railRef.current;
            if (!el) return;
            const firstCard = el.querySelector<HTMLElement>(cardSelector);
            if (!firstCard) return;
            const cardWidth = firstCard.offsetWidth;
            const clamped = Math.min(Math.max(0, targetIndex), itemCount - 1);
            const rtlSign = isRtl ? -1 : 1;
            el.scrollTo({
                left: rtlSign * clamped * (cardWidth + gap),
                behavior: "smooth",
            });
        },
        [cardSelector, gap, isRtl, itemCount],
    );

    const scrollByCard = useCallback(
        (direction: 1 | -1) => {
            scrollToIndex(activeIndex + direction);
        },
        [activeIndex, scrollToIndex],
    );

    // Keyboard nav — only when the rail itself has focus, so we never hijack page scroll.
    useEffect(() => {
        const el = railRef.current;
        if (!el) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                scrollByCard(isRtl ? 1 : -1);
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                scrollByCard(isRtl ? -1 : 1);
            }
        };
        el.addEventListener("keydown", handleKey);
        return () => el.removeEventListener("keydown", handleKey);
    }, [isRtl, scrollByCard]);

    return {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    };
};
